import { useEffect, useState } from "react";
import "./users.css";
import "./dataTable.css";
import useAuth from "../../../hooks/useAuth";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../../api/axios";
import Select from "react-select";
import { formatApplicationDate } from "./applicationDate";

const Users = () => {
  const { authState } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const restoredList = location.state?.applicationList;

  const [selectedOption, setSelectedOption] = useState(restoredList?.selectedOption || "first");
  const [filterValue, setFilterValue] = useState(Object.values(restoredList?.filters || {})[0] || "");
  const [lookupEmail, setLookupEmail] = useState("");
  const filterOptions = [
    { value: "first", label: "First Name" },
    { value: "last", label: "Last Name" },
    { value: "email", label: "Email Address" },
    { value: "school", label: "University" },
    { value: "status", label: "Application Status" },
    { value: "teamcode", label: "Team Code" },
  ];
  const selectMenuPortalTarget =
    typeof document !== "undefined" ? document.body : null;
  const adminSelectStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "rgba(104, 149, 225, 0.28)"
        : state.isFocused
          ? "rgba(255, 195, 157, 0.4)"
          : "#fffdf7",
      color: "#26385f",
      fontSize: "14px",
      ":active": {
        backgroundColor: "rgba(104, 149, 225, 0.34)",
      },
    }),
    control: (provided) => ({
      ...provided,
      minHeight: "42px",
      borderRadius: "6px",
      borderColor: "rgba(52, 69, 117, 0.48)",
      background: "#fffdf7",
      boxShadow: "none",
      color: "#26385f",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "2px 10px",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#26385f",
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: "rgba(52, 69, 117, 0.32)",
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? "#a5445b" : "#344575",
      ":hover": {
        color: "#a5445b",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#fffdf7",
      border: "1.5px solid rgba(52, 69, 117, 0.48)",
      borderRadius: "6px",
      overflow: "hidden",
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: "#fffdf7",
      paddingTop: 0,
      paddingBottom: 0,
    }),
  };

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(restoredList?.page || 1);
  const perPage = 20;
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [filters, setFilters] = useState(restoredList?.filters || {});
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const applicationList = { page, filters, selectedOption: Object.keys(filters)[0] || selectedOption };

  useEffect(() => {
    if (!authState.isAuthenticated) return;
    if (authState.user?.role !== "admin") {
      navigate("/login");
      return;
    }
    let active = true;
    setLoading(true);
    setError("");
    axiosInstance.get("/api/applications/all", {
      params: { page, per_page: perPage, filters: JSON.stringify(filters) },
    }).then(({ data }) => {
      if (!active) return;
      if (page > Math.max(1, data.pages)) {
        setPage(Math.max(1, data.pages));
        return;
      }
      setApplications(data.applications);
      setTotal(data.total);
      setPages(data.pages);
    }).catch((err) => {
      if (active) setError(err.response?.data?.message || "Unable to load applications. Please try again.");
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [authState.isAuthenticated, authState.user?.role, navigate, page, filters, retry]);

  useEffect(() => {
    navigate("/admin/users", { replace: true, state: {
      applicationList: { page, filters, selectedOption: Object.keys(filters)[0] || selectedOption },
    } });
  }, [navigate, page, filters, selectedOption]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pages) {
      setPage(newPage);
    }
  };

  const handleFilterChange = (e) => {
    setFilterValue(e.target.value);
  };

  const handleDropdownChange = (selected) => {
    const value = selected?.value || "first";
    setSelectedOption(value);
    setFilterValue("");
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setFilters(filterValue.trim() ? { [selectedOption]: filterValue.trim() } : {});
  };

  return (
    <div className="users-page">
      <div className="dashboard">
        <h1 className="ad-dash-title">Applications</h1>

        <form className="users-email-lookup" onSubmit={event => {
          event.preventDefault();
          navigate(`/admin/users/${encodeURIComponent(lookupEmail.trim().toLowerCase())}`, {
            state: { applicationList },
          });
        }}>
          <div>
            <label htmlFor="applicant-email-lookup">Find applicant by email</label>
            <input id="applicant-email-lookup" type="email" required maxLength={254}
              value={lookupEmail} onChange={event => setLookupEmail(event.target.value)}
              placeholder="Applicant's login email" />
          </div>
          <button type="submit">Open applicant</button>
        </form>
        <form className="users-searches" onSubmit={applyFilters}>
          <Select
            inputId="dropdown"
            aria-label="Filter by"
            options={filterOptions}
            value={
              filterOptions.find((option) => option.value === selectedOption) ||
              filterOptions[0]
            }
            onChange={handleDropdownChange}
            className="users-filter-select"
            classNamePrefix="users-filter-select"
            styles={adminSelectStyles}
            menuPortalTarget={selectMenuPortalTarget}
            menuPosition="fixed"
            menuPlacement="auto"
            isSearchable={false}
          />
          {selectedOption === "status" ? (
            <select className="users-find-selector" aria-label="Filter value" value={filterValue}
              onChange={handleFilterChange}>
              <option value="">All statuses</option>
              <option value="submitted">Submitted</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="waitlisted">Waitlisted</option>
              <option value="waitlist_accepted">Waitlist accepted</option>
              <option value="confirmed">Confirmed</option>
              <option value="declined">Declined</option>
            </select>
          ) : <input
            className="users-find-selector"
            name="find-selector"
            type="text"
            aria-label="Filter value"
            value={filterValue}
            maxLength={200}
            placeholder={
              selectedOption.charAt(0).toUpperCase() +
              selectedOption.slice(1).toLowerCase()
            }
            onChange={handleFilterChange}
          />}
          <button
            className="users-apply-filter"
            type="submit"
          >
            Apply Filter
          </button>
          {Object.keys(filters).length > 0 && <button type="button" className="users-apply-filter" onClick={() => {
            setFilterValue(""); setFilters({}); setPage(1);
          }}>Clear filter</button>}
        </form>

        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p role="alert">{error} <button type="button" onClick={() => setRetry((value) => value + 1)}>Retry</button></p>
        ) : applications.length === 0 ? (
          <p>No applications match this filter.</p>
        ) : (
          <div className="users-get-users">
            <div className="users-table-container">
              <table>
                <thead>
                  <tr>
                    <th>Email Address</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>University</th>
                    <th>Team Code</th>
                    <th>Submitted</th>
                    <th>Application Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {applications.map((user, index) => (
                    <tr
                      key={user.email}
                      className={index % 2 === 0 ? "even-row" : "odd-row"}
                    >
                      <td>{user.email}</td>
                      <td>{user.first}</td>
                      <td>{user.last}</td>
                      <td>{user.school}</td>
                      <td>{user.teamcode}</td>
                      <td>{formatApplicationDate(user.timestamp)}</td>
                      <td>{user.status}</td>
                      <td>
                        <Link
                          className="users-table-view"
                          to={`/admin/users/${encodeURIComponent(user.email)}`}
                          state={{ applicationList }}
                        >
                          View Application
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="users-pagination">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
                className="users-table-button"
              >
                Previous
              </button>
              <span className="users-pages">
                {" "}
                Page {page} of {pages} (Total: {total}){" "}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= pages}
                className="users-table-button"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
